const STORAGE_KEY = 'mz_welcome_offer_v1';
const SESSION_KEY = 'mz_welcome_offer_session';

/** Show after the visitor has been browsing for a short time. */
export const WELCOME_OFFER_BROWSE_DELAY_MS = 10_000;

/** Show again if they closed it this long ago. */
export const WELCOME_OFFER_REOPEN_AFTER_MS = 14 * 24 * 60 * 60 * 1000;

/** Hide longer after they actually submit the form. */
const WELCOME_OFFER_SUBMIT_HIDE_MS = 90 * 24 * 60 * 60 * 1000;

type WelcomeOfferState = {
  lastDismissedAt?: number;
  submittedAt?: number;
};

function readState(): WelcomeOfferState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as WelcomeOfferState;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeState(patch: WelcomeOfferState) {
  try {
    const next = { ...readState(), ...patch };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
}

export function wasWelcomeOfferShownThisSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function markWelcomeOfferShownThisSession() {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // ignore
  }
}

/** First visit, or last close/submit was long enough ago. */
export function shouldOfferWelcomeDialog(): boolean {
  if (typeof window === 'undefined') return false;
  if (wasWelcomeOfferShownThisSession()) return false;

  const state = readState();
  const now = Date.now();

  if (state.submittedAt && now - state.submittedAt < WELCOME_OFFER_SUBMIT_HIDE_MS) {
    return false;
  }

  if (!state.lastDismissedAt && !state.submittedAt) {
    return true;
  }

  const lastTouch = Math.max(state.lastDismissedAt || 0, state.submittedAt || 0);
  return now - lastTouch >= WELCOME_OFFER_REOPEN_AFTER_MS;
}

export function dismissWelcomeOffer() {
  markWelcomeOfferShownThisSession();
  writeState({ lastDismissedAt: Date.now() });
}

export function completeWelcomeOffer() {
  markWelcomeOfferShownThisSession();
  writeState({ submittedAt: Date.now(), lastDismissedAt: Date.now() });
}
